import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Send, ArrowLeft, Search, MessageSquare } from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';

export default function MessagesPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const selectedUserId = searchParams.get('user');
  
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  
  const messagesEndRef = useRef(null);

  // Fetch conversations
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await api.get('/messages/conversations');
        setConversations(response.data);

        // If user param is present, load that conversation
        if (selectedUserId) {
          const existingConv = response.data.find(c => c.partner.id === selectedUserId);
          if (existingConv) {
            setSelectedConversation(existingConv.partner);
          } else {
            // Fetch user info to start new conversation
            const userRes = await api.get(`/users/${selectedUserId}`);
            setSelectedConversation(userRes.data);
          }
        }
      } catch (error) {
        console.error('Error fetching conversations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConversations();
  }, [selectedUserId]);

  // Fetch messages for selected conversation
  useEffect(() => {
    if (!selectedConversation) return;

    const fetchMessages = async () => {
      try {
        const response = await api.get(`/messages/${selectedConversation.id}`);
        setMessages(response.data);
      } catch (error) {
        console.error('Error fetching messages:', error);
      }
    };

    fetchMessages();
    
    // Poll for new messages
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [selectedConversation]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Search users
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const searchUsers = async () => {
      try {
        const response = await api.get(`/search/users?q=${searchQuery}&limit=5`);
        setSearchResults(response.data.filter(u => u.id !== user.id));
      } catch (error) {
        console.error('Error searching users:', error);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, user.id]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sendingMessage) return;

    setSendingMessage(true);
    try {
      const response = await api.post('/messages', {
        recipient_id: selectedConversation.id,
        content: newMessage.trim()
      });
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      
      // Update conversation list
      setConversations(prev => {
        const existing = prev.find(c => c.partner.id === selectedConversation.id);
        if (existing) {
          return prev.map(c => 
            c.partner.id === selectedConversation.id 
              ? { ...c, last_message: response.data }
              : c
          ).sort((a, b) => new Date(b.last_message?.created_at || 0) - new Date(a.last_message?.created_at || 0));
        } else {
          return [{ partner: selectedConversation, last_message: response.data, unread_count: 0 }, ...prev];
        }
      });
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const formatMessageTime = (dateStr) => {
    const date = new Date(dateStr);
    if (isToday(date)) {
      return format(date, 'h:mm a');
    } else if (isYesterday(date)) {
      return 'Yesterday ' + format(date, 'h:mm a');
    }
    return format(date, 'MMM d, h:mm a');
  };

  const startConversation = (selectedUser) => {
    setSelectedConversation(selectedUser);
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <div className="max-w-6xl mx-auto min-[900px]:ml-60 h-[calc(100vh-3.5rem)]" data-testid="messages-page">
      <div className="flex h-full">
        {/* Conversations List */}
        <div className={`w-full md:w-80 lg:w-96 border-r border-border/40 flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
          {/* Header */}
          <div className="p-4 border-b border-border/40">
            <h1 className="font-heading text-xl font-bold mb-4">Messages</h1>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-full bg-muted/50 border-transparent"
                data-testid="search-users-input"
              />
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl overflow-hidden shadow-lg z-10">
                  {searchResults.map(searchUser => (
                    <button
                      key={searchUser.id}
                      onClick={() => startConversation(searchUser)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-muted transition-colors"
                    >
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={searchUser.avatar} />
                        <AvatarFallback>{searchUser.display_name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div className="text-left">
                        <p className="font-semibold">{searchUser.display_name}</p>
                        <p className="text-sm text-muted-foreground">@{searchUser.username}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Conversations */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-4 border-b border-border/20">
                  <Skeleton className="w-12 h-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                </div>
              ))
            ) : conversations.length === 0 ? (
              <div className="text-center py-12 px-4">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No conversations yet</p>
                <p className="text-sm text-muted-foreground mt-1">Search for users to start chatting</p>
              </div>
            ) : (
              conversations.map(conv => (
                <button
                  key={conv.partner.id}
                  onClick={() => setSelectedConversation(conv.partner)}
                  className={`w-full flex items-center gap-3 p-4 border-b border-border/20 hover:bg-muted/50 transition-colors ${
                    selectedConversation?.id === conv.partner.id ? 'bg-muted' : ''
                  }`}
                  data-testid={`conversation-${conv.partner.username}`}
                >
                  <div className="relative">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={conv.partner.avatar} />
                      <AvatarFallback>{conv.partner.display_name?.[0]}</AvatarFallback>
                    </Avatar>
                    {conv.unread_count > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="font-semibold truncate">{conv.partner.display_name}</p>
                    <p className="text-sm text-muted-foreground truncate">
                      {conv.last_message?.content || 'Start a conversation'}
                    </p>
                  </div>
                  {conv.last_message && (
                    <span className="text-xs text-muted-foreground">
                      {formatMessageTime(conv.last_message.created_at)}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col ${selectedConversation ? 'flex' : 'hidden md:flex'}`}>
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-border/40 flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden"
                  onClick={() => setSelectedConversation(null)}
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <Link to={`/profile/${selectedConversation.username}`} className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={selectedConversation.avatar} />
                    <AvatarFallback>{selectedConversation.display_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold">{selectedConversation.display_name}</p>
                    <p className="text-sm text-muted-foreground">@{selectedConversation.username}</p>
                  </div>
                </Link>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                        msg.sender_id === user.id
                          ? 'bg-primary text-white rounded-br-sm'
                          : 'bg-muted rounded-bl-sm'
                      }`}
                    >
                      <p>{msg.content}</p>
                      <p className={`text-xs mt-1 ${
                        msg.sender_id === user.id ? 'text-white/70' : 'text-muted-foreground'
                      }`}>
                        {formatMessageTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-border/40">
                <div className="flex gap-2">
                  <Input
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="flex-1 rounded-full bg-muted/50 border-transparent"
                    data-testid="message-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={!newMessage.trim() || sendingMessage}
                    className="rounded-full w-12 h-12 p-0"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
                <MessageSquare className="w-10 h-10 text-muted-foreground" />
              </div>
              <h2 className="font-heading text-xl font-bold mb-2">Your Messages</h2>
              <p className="text-muted-foreground max-w-sm">
                Select a conversation or search for users to start chatting
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
